// Daily canvas areas — pure helpers (Oct 23, 2026).
//
// Greg: "get James to assign a daily area for each person … list if they have
// a build or adjuster meeting that day, that is where the team member will be
// working … a button to create an assigned area for the day around the build
// or adjuster meeting … a daily map of that day's areas … expire an area and
// create a new area each day … at the end of each week and month a review of
// areas."
//
// An area now carries an optional `for_date` (a daily area; null = a standing
// area, exactly what every area was before) and an optional anchor: the deal
// whose build or adjuster meeting it was drawn around. Nothing expires on its
// own — the Today tab shows yesterday's areas that are still open and James
// taps Expire or Carry to today.
//
// Everything here is pure (no supabase, no React) so it is unit-tested; the
// data calls live in lib/canvasAreas.ts.

import type { AreaStats } from "./canvasAreas";

export type AnchorKind = "build" | "adjuster" | "reinspection" | "other";

/** One place a person has to be on a day: a build, an adjuster meeting or a reinspection. */
export interface DayStop {
  deal_id: string;
  kind: "build" | "adjuster" | "reinspection";
  /** YYYY-MM-DD (Eastern) */
  day: string;
  /** ISO timestamp of the meeting; null for a build (all day). */
  at: string | null;
  /** "8:00 am" as the office entered it; null for a build. */
  when: string | null;
  owner_id: string | null;
  split_rm_id: string | null;
  homeowner_name: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  lat: number | null;
  lon: number | null;
  stage: string | null;
}

export const KIND_LABEL: Record<AnchorKind, string> = {
  build: "Build",
  adjuster: "Adjuster",
  reinspection: "Reinspection",
  other: "Stop",
};

const KIND_ORDER: Record<string, number> = { build: 0, adjuster: 1, reinspection: 2 };

/** The stops that put this person somewhere today: deals they own or split. */
export function stopsFor(stops: DayStop[], personId: string): DayStop[] {
  return stops
    .filter((s) => s.owner_id === personId || s.split_rm_id === personId)
    .sort((a, b) => (a.at ?? "").localeCompare(b.at ?? "") || (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9));
}

/** The other person on a split deal, if any. */
export function partnerId(stop: DayStop, personId: string): string | null {
  if (stop.owner_id && stop.owner_id !== personId) return stop.owner_id;
  if (stop.split_rm_id && stop.split_rm_id !== personId) return stop.split_rm_id;
  return null;
}

export function lastName(full: string | null | undefined): string {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  // "LANCE CAROLE A" style parcel names: first token is the surname.
  if (parts.length >= 2 && parts.every((p) => p === p.toUpperCase() && /[A-Z]/.test(p))) {
    return parts[0][0] + parts[0].slice(1).toLowerCase();
  }
  return parts[parts.length - 1];
}

/** "3995 Idlewilde Meadows Dr NE" → "Idlewilde Meadows Dr" (no house number, no trailing direction). */
export function streetName(street: string | null | undefined): string {
  let s = (street ?? "").trim().replace(/\s+/g, " ");
  s = s.replace(/^\d+[a-z]?(-\d+)?\s+/i, "");
  s = s.replace(/\s+(N|S|E|W|NE|NW|SE|SW)\.?$/i, "");
  return s;
}

/** The name a daily area gets when it is drawn around a stop: "Woodstock · Pioneer Dr". */
export function areaNameFromStop(stop: Pick<DayStop, "city" | "street">): string {
  const city = (stop.city ?? "").trim();
  const st = streetName(stop.street);
  return [city, st].filter(Boolean).join(" · ") || "Daily area";
}

/** "Adjuster 8:00 am · Butterworth" / "Build · Sarkar" */
export function stopTitle(stop: DayStop): string {
  const who = lastName(stop.homeowner_name);
  const head = stop.kind === "build" ? "Build" : `${KIND_LABEL[stop.kind]}${stop.when ? ` ${stop.when}` : ""}`;
  return who ? `${head} · ${who}` : head;
}

/** "117 Pioneer Dr, Woodstock 30188" */
export function stopAddress(stop: Pick<DayStop, "street" | "city" | "zip">): string {
  const street = (stop.street ?? "").trim();
  const place = [stop.city?.trim(), stop.zip?.trim()].filter(Boolean).join(" ");
  return [street, place].filter(Boolean).join(", ");
}

export function hasCoords(stop: Pick<DayStop, "lat" | "lon">): stop is DayStop & { lat: number; lon: number } {
  return Number.isFinite(Number(stop.lat)) && Number.isFinite(Number(stop.lon)) && Number(stop.lat) !== 0 && Number(stop.lon) !== 0;
}

// ── Dates (all YYYY-MM-DD, Eastern days) ────────────────────────────────

export function addDays(day: string, n: number): string {
  const d = new Date(day + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Monday to Sunday around `day`. */
export function weekRange(day: string): { from: string; to: string } {
  const dow = new Date(day + "T12:00:00Z").getUTCDay(); // 0 = Sun
  const from = addDays(day, dow === 0 ? -6 : 1 - dow);
  return { from, to: addDays(from, 6) };
}

export function monthRange(day: string): { from: string; to: string } {
  const from = day.slice(0, 8) + "01";
  const d = new Date(from + "T12:00:00Z");
  d.setUTCMonth(d.getUTCMonth() + 1);
  return { from, to: addDays(d.toISOString().slice(0, 10), -1) };
}

/** Every day in a range, oldest first. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to && out.length < 400; d = addDays(d, 1)) out.push(d);
  return out;
}

export function shiftRange(range: { from: string; to: string }, mode: "week" | "month", dir: -1 | 1): { from: string; to: string } {
  if (mode === "week") return weekRange(addDays(range.from, 7 * dir));
  const d = new Date(range.from + "T12:00:00Z");
  d.setUTCMonth(d.getUTCMonth() + dir);
  return monthRange(d.toISOString().slice(0, 10));
}

export function rangeLabel(range: { from: string; to: string }, mode: "week" | "month"): string {
  const f = new Date(range.from + "T12:00:00Z"), t = new Date(range.to + "T12:00:00Z");
  if (mode === "month") return f.toLocaleDateString("en-US", { timeZone: "UTC", month: "long", year: "numeric" });
  const sameMonth = f.getUTCMonth() === t.getUTCMonth();
  const a = f.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
  const b = t.toLocaleDateString("en-US", { timeZone: "UTC", month: sameMonth ? undefined : "short", day: "numeric" });
  return `Week of ${a} – ${b}`;
}

// ── Daily vs standing ───────────────────────────────────────────────────

export type AreaDayState = "today" | "past" | "future" | "standing";

/** Today's date in Eastern time, YYYY-MM-DD. */
export function etToday(now: Date = new Date()): string {
  return now.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

/**
 * How an area relates to the day on screen. A standing area has no date.
 * "past" means an older day's area still open — older than the day on screen
 * AND older than real today, so looking ahead a day never turns today's live
 * areas into something to expire or carry.
 */
export function areaDayState(a: Pick<AreaStats, "for_date">, day: string, today: string = etToday()): AreaDayState {
  if (!a.for_date) return "standing";
  if (a.for_date === day) return "today";
  if (a.for_date < day && a.for_date < today) return "past";
  return "future";
}

const STATE_ORDER: Record<AreaDayState, number> = { today: 0, past: 1, future: 2, standing: 3 };

/** Today's areas first, then older daily ones still open, then standing areas. */
export function sortAreasForDay<T extends Pick<AreaStats, "for_date" | "first_assigned_at">>(areas: T[], day: string, today: string = etToday()): T[] {
  return [...areas].sort((a, b) =>
    STATE_ORDER[areaDayState(a, day, today)] - STATE_ORDER[areaDayState(b, day, today)] ||
    (b.for_date ?? "").localeCompare(a.for_date ?? "") ||
    b.first_assigned_at.localeCompare(a.first_assigned_at));
}

/** Daily areas from an earlier day that nobody has expired yet. */
export function openPastAreas<T extends Pick<AreaStats, "for_date">>(areas: T[], day: string, today: string = etToday()): T[] {
  return areas.filter((a) => areaDayState(a, day, today) === "past");
}

/**
 * The active area drawn around this stop that `personId` is on — for the day
 * on screen when `day` is given (an un-expired area from another day around
 * the same deal doesn't count). Omit `personId` to find anyone's.
 */
export function areaForStop(areas: AreaStats[], stop: Pick<DayStop, "deal_id">, personId?: string, day?: string): AreaStats | undefined {
  return areas.find((a) =>
    a.anchor_deal_id === stop.deal_id &&
    (!personId || a.reps.some((r) => r.user_id === personId)) &&
    (!day || !a.for_date || a.for_date === day));
}

/** A stable key for "which areas are these" — changes only when an area is added or removed. */
export function areaIdsKey(areas: ReadonlyArray<Pick<AreaStats, "area_group_id">> | null | undefined): string {
  return (areas ?? []).map((a) => a.area_group_id).sort().join(",");
}

/** Pin colors for the day map: one per daily area, stable by position. */
export const AREA_COLORS = ["#f46530", "#345c72", "#1b7fa9", "#2f855a", "#b7791f", "#7c3aed", "#c53030", "#0e7490"];

export function colorFor(index: number): string {
  return AREA_COLORS[index % AREA_COLORS.length];
}

// ── The canvas deep link ────────────────────────────────────────────────

export interface DrawAreaOptions {
  /** Land the map here (the stop), zoomed to the street. */
  lat?: number | null;
  lon?: number | null;
  zoom?: number;
  /** The deal the area is drawn around. */
  anchorDealId?: string | null;
  anchorKind?: AnchorKind | null;
  /** Make it a daily area for this day. */
  forDate?: string | null;
  /** Prefill the area name. */
  label?: string | null;
}

/** The query string behind /canvas?… that opens draw mode for a person, landed on their stop. */
export function drawAreaSearch(memberId: string, opts: DrawAreaOptions = {}): string {
  const q = new URLSearchParams({ assign: "1", rep: memberId });
  const lat = Number(opts.lat), lon = Number(opts.lon);
  if (Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0) {
    q.set("lat", lat.toFixed(6));
    q.set("lon", lon.toFixed(6));
    q.set("z", String(opts.zoom ?? 16));
  }
  if (opts.anchorDealId) q.set("anchor", opts.anchorDealId);
  if (opts.anchorKind) q.set("kind", opts.anchorKind);
  if (opts.forDate) q.set("for", opts.forDate);
  if (opts.label) q.set("label", opts.label.slice(0, 80));
  return q.toString();
}

// ── Review (week / month) ───────────────────────────────────────────────

export interface ReviewRep { user_id: string; full_name: string; role: string | null; active: boolean }
export interface ReviewDayPerson { user_id: string; full_name: string; knocks: number; talked: number; assigned: boolean }
export interface ReviewDay { d: string; knocks: number; talked: number; appts: number; people?: ReviewDayPerson[] }
export interface ReviewPerson {
  user_id: string; full_name: string; role: string | null; assigned: boolean;
  knocks: number; talked: number; appts: number; days: number;
}
export interface ReviewArea {
  area_group_id: string;
  area_label: string | null;
  status: "active" | "removed";
  first_assigned_at: string;
  removed_at: string | null;
  for_date: string | null;
  anchor_kind: AnchorKind | null;
  anchor: { street: string | null; city: string | null } | null;
  storm_tied: boolean;
  geometry: unknown | null;
  center: { lat: number; lon: number };
  bbox: [number, number, number, number];
  sq_mi: number;
  pin_count: number;
  cities: Array<{ city: string; n: number }>;
  zips: Array<{ zip: string; n: number }>;
  reps: ReviewRep[];
  by_day: ReviewDay[];
  by_person: ReviewPerson[];
  totals: { knocks: number; talked: number; appts: number; days: number; pins_knocked: number };
  deals: { deals: number; claims: number; builds: number };
}

export function reviewAreaName(a: Pick<ReviewArea, "area_label" | "cities" | "zips" | "anchor">): string {
  if (a.area_label?.trim()) return a.area_label.trim();
  if (a.anchor && (a.anchor.city || a.anchor.street)) return areaNameFromStop(a.anchor);
  const city = a.cities[0]?.city, zip = a.zips[0]?.zip;
  return [city, zip].filter(Boolean).join(" · ") || "Unnamed area";
}

export interface ReviewDayRow {
  day: string;
  areas: Array<{ area: ReviewArea; knocks: number; talked: number; appts: number; people: ReviewDayPerson[] }>;
  knocks: number; talked: number; appts: number;
}

/** One row per day in the window, areas with knocks that day under it (busiest first), with who knocked THAT day. */
export function reviewDayRows(areas: ReviewArea[], from: string, to: string): ReviewDayRow[] {
  return daysBetween(from, to).map((day) => {
    const rows = areas.flatMap((a) => {
      const d = a.by_day.find((x) => x.d === day);
      if (!d || d.knocks === 0) return [];
      return [{ area: a, knocks: d.knocks, talked: d.talked, appts: d.appts, people: d.people ?? [] }];
    }).sort((x, y) => y.knocks - x.knocks);
    return {
      day, areas: rows,
      knocks: rows.reduce((s, r) => s + r.knocks, 0),
      talked: rows.reduce((s, r) => s + r.talked, 0),
      appts: rows.reduce((s, r) => s + r.appts, 0),
    };
  });
}

export interface ReviewPersonRow {
  user_id: string; full_name: string; role: string | null;
  areas: Array<{ area: ReviewArea; assigned: boolean; knocks: number }>;
  knocks: number; talked: number; appts: number; days: number;
}

/** Everyone who knocked an area in the window, busiest first; people assigned but idle at the end. */
export function reviewPersonRows(areas: ReviewArea[]): ReviewPersonRow[] {
  const map = new Map<string, ReviewPersonRow>();
  const get = (p: { user_id: string; full_name: string; role: string | null }) => {
    let r = map.get(p.user_id);
    if (!r) { r = { user_id: p.user_id, full_name: p.full_name, role: p.role, areas: [], knocks: 0, talked: 0, appts: 0, days: 0 }; map.set(p.user_id, r); }
    return r;
  };
  for (const a of areas) {
    for (const p of a.by_person) {
      const r = get(p);
      r.areas.push({ area: a, assigned: p.assigned, knocks: p.knocks });
      r.knocks += p.knocks; r.talked += p.talked; r.appts += p.appts; r.days = Math.max(r.days, p.days);
    }
    for (const rep of a.reps) {
      const r = get(rep);
      if (!r.areas.some((x) => x.area.area_group_id === a.area_group_id)) r.areas.push({ area: a, assigned: true, knocks: 0 });
    }
  }
  return [...map.values()].sort((x, y) => y.knocks - x.knocks || x.full_name.localeCompare(y.full_name));
}

export function reviewTotals(areas: ReviewArea[]) {
  const worked = areas.filter((a) => a.totals.knocks > 0);
  const areaDays = areas.reduce((s, a) => s + a.by_day.filter((d) => d.knocks > 0).length, 0);
  const knocks = areas.reduce((s, a) => s + a.totals.knocks, 0);
  const talked = areas.reduce((s, a) => s + a.totals.talked, 0);
  const appts = areas.reduce((s, a) => s + a.totals.appts, 0);
  const knockers = new Set(areas.flatMap((a) => a.by_person.filter((p) => p.knocks > 0).map((p) => p.user_id))).size;
  const assigned = new Set(areas.flatMap((a) => a.reps.map((r) => r.user_id))).size;
  return { areas: areas.length, worked: worked.length, areaDays, knocks, talked, appts, knockers, assigned,
    talkRate: knocks > 0 ? Math.round((talked / knocks) * 100) : null };
}

/** Days in the window a person could have worked (Mon–Fri). */
export function workdaysIn(from: string, to: string): number {
  return daysBetween(from, to).filter((d) => { const dow = new Date(d + "T12:00:00Z").getUTCDay(); return dow >= 1 && dow <= 5; }).length;
}

function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Day-by-day CSV of the window: one line per area per day with knocks. */
export function reviewCsv(areas: ReviewArea[], from: string, to: string): string {
  const lines = [["Day", "Area", "Kind", "Assigned to", "Pins", "Knocks", "Talked", "Appointments", "Who knocked"].join(",")];
  for (const row of reviewDayRows(areas, from, to)) {
    for (const r of row.areas) {
      lines.push([
        row.day, reviewAreaName(r.area), r.area.for_date ? "daily" : "standing",
        r.area.reps.map((x) => x.full_name).join("; "), r.area.pin_count, r.knocks, r.talked, r.appts,
        r.people.map((p) => `${p.full_name} ${p.knocks}`).join("; "),
      ].map(csvCell).join(","));
    }
  }
  return lines.join("\n") + "\n";
}
